import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { getTemplateSampleBoostingNewsExcel, importGenerateBoostingNewsByExcel } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    isOpen: boolean;
    onRefresh?: () => void,
    onClose?: () => void,
}

registerLocale("en", en);
registerLocale("ko", ko);

const FILE_SIZE = 10 * 1024 * 1024; // 10MB
const SUPPORTED_FORMATS = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ModalImportGenerateBoostingNews = ({
    isOpen,
    onRefresh,
    onClose,
}: Props) => {
    const { t, i18n } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isLoadingDownloadFile, setIsLoadingDownloadFile] = useState<boolean>(false);

    const [isOpenNotification, setIsOpenNotification] = useState<boolean>(false);
    const [articleNotification, setArticleNotification] = useState<{ error_notifications: string[], total: number } | null>({ error_notifications: [], total: 0 });


    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const data = {
                files: values?.file || null,
            };
            const response: any = await importGenerateBoostingNewsByExcel(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                const error_notifications = (response?.data || [])?.filter((item: any) => item?.errors?.length > 0);
                const total = (response?.data || [])?.length;
                if (error_notifications?.length > 0) {
                    setArticleNotification(() => ({
                        error_notifications: error_notifications,
                        total: total,
                    }));
                    handleShowNotification();

                } else {
                    toast(
                        `${t("The process has been completed.")}`,
                        CONFIG_OPTION_TOAST_NORMAL
                    );
                }
                onRefresh && onRefresh();

            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        file: Yup.mixed()
            .test(
                'fileSize',
                t('File too large. Max size is 10MB.'),
                (value: any) => !value || (value?.size <= FILE_SIZE)
            )
            .test(
                'fileFormat',
                t('Unsupported Format. Please upload an Excel file.'),
                (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
            ).required(`${t("File is required")}`),
    });

    const formik = useFormik({
        initialValues: {
            file: null,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });


    const handleChangeFile = (value: any | null) => {
        formik?.setFieldValue('file', value?.target?.files[0] || null);
    }

    const handleDownloadExcel = async (e: any) => {
        try {
            e.preventDefault();
            if (isLoadingDownloadFile) {
                return;
            }
            setIsLoadingDownloadFile((_prev) => true);
            const response: any = await getTemplateSampleBoostingNewsExcel({});
            if (response?.data) {
                setIsLoadingDownloadFile((_prev) => false);
                const link = document.createElement('a');
                link.href = response?.data?.import_file_url;
                link.download = 'Download Sample Excel';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setIsLoadingDownloadFile((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoadingDownloadFile((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }

    const handleClose = () => {
        formik.resetForm();
        formik.setErrors({});
        onClose && onClose();
    }

    const handleShowNotification = () => {
        setIsOpenNotification((_prev) => true);
    }

    const handleCloseNotification = () => {
        setIsOpenNotification((_prev) => false);
    }

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="md"
                scrollable={false}
            >
                <ModalHeader toggle={handleClose}>
                    <>{t("Button Import Bulk Generate Boosting News V2")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <div>
                        <Container>
                            <Row>
                                <form onSubmit={formik.handleSubmit}>
                                    <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                                        <Row>
                                            <Col sm={12} md={12} xl={12} className='mt-3 '>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <Label htmlFor="formSizeDefault" className="form-label">{t('Bulk Articles') + ' (' + t('Excel File') + ')'} <span className="text-danger"> *</span></Label>
                                                </div>
                                                <div className="custom-file-button">
                                                    <Input className="form-control" accept=".xls,.xlsx"
                                                        label="Upload Your File"
                                                        data-title="Upload Your File"
                                                        id="formSizeDefault"
                                                        type="file"
                                                        onChange={handleChangeFile}
                                                    />
                                                    {!formik?.values?.file && <div className="text-upload">{t('No file chosen')}</div>}
                                                    <span className="icon-upload"> <i className="ri-upload-line" /></span>
                                                </div>
                                                {formik.touched.file && formik.errors.file ? (
                                                    <div className="text-danger mt-2">{formik.errors.file}</div>
                                                ) : null}
                                            </Col>
                                            <Col sm={6} md={6} xl={6} className='mt-0 mt-sm-4 text-start ' style={{ paddingTop: '26px' }}>
                                                <a href={'#'} onClick={handleDownloadExcel} download="기사_등록_샘플_파일.xlsx" className="stretched-link hover-underline">
                                                    {isLoadingDownloadFile && <Spinner size="sm me-1" ></Spinner>} {t('Download Sample Excel')}
                                                </a>
                                            </Col>
                                            <Col sm={6} md={6} xl={6} className='mt-4 pt-1 text-end'>
                                                <button
                                                    style={{ width: '160px' }}
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="btn btn-primary fs-14 rounded-pill">
                                                    {isLoading ? <Spinner size="sm me-1" ></Spinner> : <></>}
                                                    {t('Button Import Bulk')}
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>
                                </form>
                            </Row >
                        </Container >
                    </div>
                </ModalBody>
            </Modal>
            <Modal
                isOpen={isOpenNotification}
                centered={true}
                size="lg"
                scrollable={false}
            >
                <ModalHeader toggle={handleCloseNotification} >{t('Notification')}</ModalHeader>
                <ModalBody>
                    <div className="d-flex flex-column gap-2 font-weight-bold mb-2 fs-16 text-muted">{t('Article Errors')}: {articleNotification?.error_notifications?.length} / {articleNotification?.total}</div>
                    <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>{t('Keyword')}</th>
                                    <th style={{ maxWidth: '200px' }}>{t('Error')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articleNotification?.error_notifications?.map((item: any) => (
                                    <tr key={item?.keyword}>
                                        <td>{item?.keyword || '-'}</td>
                                        <td className="text-start text-danger">{item?.errors?.join(', ') || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="button"
                        className="btn btn-secondary fs-14 rounded-pill"
                        style={{ width: '100px' }}
                        onClick={handleCloseNotification}
                    >
                        {t('Button Close')}
                    </button>
                </ModalFooter>
            </Modal>
        </React.Fragment >
    );
};

export default ModalImportGenerateBoostingNews;
